import React, { FC } from 'react'

//* styles
import {
  Checkbox,
  FilterGroup,
  MoreResourcesContainer,
  ResourceListingLeftFilterContainer,
  ToggleText,
} from '../style'

//* utils
import { ASSETS } from '../../../../../constants/assets'
import { LocaleCodes } from '../../../../../constants/localization.constants'
import { BlueArrowDown, RedX } from '../../../../../constants/svg'
import { kebabCaseString } from '../../../../../utils/functions.utils'
import ImgUtil from '../../../../../utils/img.utils'
import formatInlineCode from '../../../../../utils/shortTextFormatting'
import { toggleDrawer } from '../utils/toggle'

//* components
import Button from '../../../../global/button'
import Typography from '../../../../global/typography'
import WrapperImage from '../../../../global/wrapperImage'

type Props = {
  filtersToShow: any
  numResults: number
  listingPageData: any
  checkboxFilters: any
  handleFilterItemClick: any
  filterDrawers: any
  isEvents: boolean
  mobileDrawerOpen: any
  defaultLocale: string
  hasFiltersSelected: any
  handleDrawerOpen: any
  resetFilterState: any
  eventsContentBlockHeadline: string
  eventPageContentBlock: string
  microcopyData: any
}

export const RenderFilters: FC<Props> = ({
  filtersToShow,
  numResults,
  listingPageData,
  checkboxFilters,
  handleFilterItemClick,
  filterDrawers,
  isEvents,
  mobileDrawerOpen,
  defaultLocale,
  hasFiltersSelected,
  handleDrawerOpen,
  resetFilterState,
  eventsContentBlockHeadline,
  eventPageContentBlock,
  microcopyData,
}) => {
  let checkedFilters: any = []
  let filterStateIdx: number = 0

  if (listingPageData.filters?.length > 0) {
    const filtersToDisplay = listingPageData.filters?.map((filterGroup: any, n: number) => {
      const filterName = filterGroup.name || ''
      const typeName = filterGroup.items?.length && filterGroup.items[0]?.__typename
      const primaryItems: any = []
      const secondaryItems: any = []

      if (filterGroup.items?.length !== 0) {
        let filtered = filterGroup.items || []

        for (let k = 0; k < filtered.length; k++) {
          if (filtered[k]) {
            const item = filtered[k]
            const filterItemName = item?.title || item?.customerName || ''
            const strippedValue = kebabCaseString(filterItemName) || ''
            const isChecked = (checkboxFilters && checkboxFilters[filterStateIdx]?.checked) || false

            const filterElement = (
              <Checkbox htmlFor={filterItemName} key={`Filter-Item--${filterItemName}`}>
                <input
                  id={filterItemName}
                  type="checkbox"
                  value={strippedValue}
                  className="Checkbox__Input"
                  data-item={filterName}
                  data-idx={filterStateIdx}
                  data-group-idx={n}
                  onChange={handleFilterItemClick}
                  checked={isChecked}
                />

                <Typography type="body" className="Checkbox__Label">
                  {filterItemName}
                </Typography>
              </Checkbox>
            )

            if (isChecked) {
              checkedFilters.push(filterElement)
            }

            if (
              (filtersToShow && filtersToShow[typeName]?.has(filtered[k].title)) ||
              !filtersToShow
            ) {
              if (primaryItems?.length <= 3) {
                primaryItems.push(filterElement)
              } else {
                secondaryItems.push(filterElement)
              }
            }
          }

          filterStateIdx++
        }
      }

      if (primaryItems?.length) {
        return (
          <FilterGroup
            key={`Filter-Group--${n}`}
            ref={drawer => {
              filterDrawers.current[filterName] = drawer
            }}>
            <Typography type="header6" className="Filter-Name">
              {filterName}
            </Typography>

            {primaryItems}
            {secondaryItems?.length !== 0 && (
              <>
                <div className="Filter-Group__Secondary-Items">{secondaryItems}</div>

                <ToggleText className={`Toggle-Text--${n}`} $useCase={listingPageData.useCase}>
                  <button
                    onClick={() => toggleDrawer(n, 'close', filterDrawers)}
                    className="Toggle-Text__Button Toggle-Text__Button--Less">
                    <BlueArrowDown isEvents={isEvents} />

                    <Typography
                      className="Toggle-Text__Label Toggle-Text__Label--Less"
                      type="bodyXS">
                      {microcopyData.showLessText()}
                    </Typography>
                  </button>

                  <button
                    onClick={() => toggleDrawer(n, 'open', filterDrawers)}
                    className="Toggle-Text__Button Toggle-Text__Button--More">
                    <BlueArrowDown isEvents={isEvents} />

                    <Typography
                      className="Toggle-Text__Label Toggle-Text__Label--More"
                      type="bodyXS">
                      {microcopyData.showMoreText()}
                    </Typography>
                  </button>
                </ToggleText>
              </>
            )}
          </FilterGroup>
        )
      }
    })

    return (
      <ResourceListingLeftFilterContainer
        $useCase={listingPageData.useCase}
        $mobileDrawerOpen={mobileDrawerOpen}
        $defaultLocale={defaultLocale}
        $displayFilters={hasFiltersSelected() || numResults > 0}>
        <div>
          <div className="ResourceListing__Filters-Mobile-Actions">
            <Button
              icon={false}
              clickHandler={handleDrawerOpen}
              text={microcopyData.applyFiltersText()}
              type={'primary'}
              size={'small'}
              theme={'light'}
            />

            <button onClick={handleDrawerOpen}>
              <WrapperImage
                src={
                  isEvents
                    ? ImgUtil.getResizedImageUrl(ASSETS.X_ICON_WHITE, '')
                    : ImgUtil.getResizedImageUrl(ASSETS.ERRORS, '')
                }
                alt="Close icon"
                className="ResourceListing__Filters-Close-Icon"
                width={48}
                height={48}
                threshold={650}
              />
            </button>
          </div>

          <div className="ResourceListing__Filters-Inner-Container">
            {checkedFilters && checkedFilters.length > 0 && (
              <>
                <Typography type="captionAlt" className="Filter-By__Label">
                  {microcopyData.activeFiltersText()}
                </Typography>

                {checkedFilters}

                <ToggleText
                  className="Toggle-Text--Margin-Bottom"
                  $useCase={listingPageData.useCase}
                  onClick={() => resetFilterState(false)}>
                  <RedX />

                  <Typography className="Toggle-Text__Label-Black" type="bodyXS">
                    {microcopyData.clearFiltersText()}
                  </Typography>
                </ToggleText>
              </>
            )}

            <div className={numResults === 0 ? 'Filters--hide' : ''}>
              <Typography type="captionAlt" className="Filter-By__Label">
                {microcopyData.filterByText()}
              </Typography>

              {filtersToDisplay}
            </div>

            <Typography type="header6" className="Filter-Name">
              {formatInlineCode(eventsContentBlockHeadline)}
            </Typography>

            {eventPageContentBlock}
          </div>

          {defaultLocale !== LocaleCodes.ENGLISH.code &&
            microcopyData.wantMore() &&
            microcopyData.allResources() && (
              <MoreResourcesContainer>
                <Typography
                  type="bodyXS"
                  className={`ResourceListing__Filters-Want-More-Text--${
                    isEvents ? 'dark' : 'light'
                  }`}>
                  {microcopyData.wantMore()}
                </Typography>

                <Button
                  type="text-link"
                  theme={isEvents ? 'dark' : 'light'}
                  text={microcopyData.allResources()}
                  icon={true}
                  href={`/resources/all`}
                />
              </MoreResourcesContainer>
            )}
        </div>
      </ResourceListingLeftFilterContainer>
    )
  } else {
    return null
  }
}
